import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { companyService } from '@/services/company.service'
import { maskCnpj } from '@/lib/validators'
import type { Company } from '@/types/company.types'

import { Page } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'

function IconPlus() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  )
}

function IconEye() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  )
}

function IconGlobe() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
    </svg>
  )
}

function IconEdit() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  )
}

export function CompaniesPage() {
  const navigate = useNavigate()
  const [companies, setCompanies] = useState<Company[]>([])
  const [loading, setLoading] = useState(true)

  async function fetchCompanies() {
    setLoading(true)
    try {
      const list = await companyService.list()
      setCompanies(Array.isArray(list) ? list : [])
    } catch {
      setCompanies([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchCompanies() }, [])

  return (
    <Page>
      <Page.Header
        title="Empresas"
        subtitle="Gerencie as empresas que você administra"
        action={
          <Button onClick={() => navigate('/dashboard/companies/new')} icon={<IconPlus />}>
            Nova Empresa
          </Button>
        }
      />

      {loading ? (
        <Table.Wrapper standalone={true} exportFilename="companies">
          <Table.Skeleton rows={5} />
        </Table.Wrapper>
      ) : companies.length === 0 ? (
        <Table.Wrapper standalone={true} exportFilename="companies">
          <Table.EmptyState
            message="Nenhuma empresa cadastrada ainda."
            action={
              <Button onClick={() => navigate('/dashboard/companies/new')}>
                Cadastrar primeira empresa
              </Button>
            }
          />
        </Table.Wrapper>
      ) : (
        <Table.Wrapper standalone={true} exportFilename="companies">
          <Table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>CNPJ</th>
                <th>Cadastrada em</th>
                <th style={{ textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {companies.map(company => (
                <tr key={company.id}>
                  <td className="ui-table__name" data-label="Nome">
                    {company.name || '—'}
                    {company.is_master && (
                      <Table.Badge variant="success">Master</Table.Badge>
                    )}
                  </td>
                  <td data-label="CNPJ" style={{ fontFamily: 'monospace' }}>
                    {maskCnpj(company.cnpj || '')}
                  </td>
                  <td data-label="Cadastrada em">
                    {company.created_at
                      ? new Date(company.created_at).toLocaleDateString('pt-BR')
                      : '—'}
                  </td>
                  <td data-label="Ações">
                    <div className="ui-table__actions">
                      {Boolean(company.is_whitelabel || company.is_whitelabel_master || company.is_master) && (
                        <Table.ActionButton
                          title="Visualizar Landing Page"
                          onClick={() => window.open(`/?tenant=${company.slug || company.id}&preview=true`, '_blank')}
                          icon={<IconGlobe />}
                        />
                      )}
                      <Table.ActionButton
                        title="Detalhes"
                        onClick={() => navigate(`/dashboard/companies/${company.id}`, { state: { company } })}
                        icon={<IconEye />}
                      />
                      <Table.ActionButton
                        title="Editar"
                        onClick={() => navigate(`/dashboard/companies/${company.id}/edit`, { state: { company } })}
                        icon={<IconEdit />}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Table.Wrapper>
      )}
    </Page>
  )
}
